import * as serviceWorkerRegistration from '../../../serviceWorkerRegistration';
import { useEffect, useRef, useState } from 'react';
import Dialog from '../../../components/envago/Dialog/Dialog';
import Button from '../../../components/envago/Button/Button';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { offlineSelectors } from '../../../redux/offline';
import queueSelectors from '../../../redux/offline/queue.selectors';
import { reloadPage } from './reloadPage';
import {
    hasUserInteracted,
    rememberAutoUpdate,
    requestActivationIfSoleClient,
    waitForActivation,
    wasAutoUpdatedRecently,
} from './autoUpdate';

const CheckUpdates = () => {
    const { t } = useTranslation();

    const isOnline = useSelector(offlineSelectors.isOnline);
    const isOnlineRef = useRef(isOnline);
    useEffect(() => { isOnlineRef.current = isOnline; }, [isOnline]);

    // Offline queue: a reload while readings are pending or being sent could interrupt a request
    const isQueueSending = useSelector(offlineSelectors.isQueueSending);
    const queueLength = useSelector(queueSelectors.getQueueLength);
    const queueBusyRef = useRef(false);
    queueBusyRef.current = isQueueSending || queueLength > 0;

    const [dialogVisible, setDialogVisible] = useState(false);
    const [serviceWorker, setServiceWorker] = useState<ServiceWorker>();

    useEffect(() => {
        const showDialog = (waitingServiceWorker: ServiceWorker | null) => {
            if (waitingServiceWorker && isOnlineRef.current) {
                setDialogVisible(true);
                setServiceWorker(waitingServiceWorker);
            }
        };

        serviceWorkerRegistration.register({
            onUpdate: (registration) => showDialog(registration.waiting),

            // The update was downloaded in an earlier session and the page was just (re)opened, so there is
            // nothing to lose yet: load the new version right away instead of asking – but only if it is safe.
            onWaitingAtStartup: async (registration) => {
                const waitingServiceWorker = registration.waiting;
                if (!waitingServiceWorker) return;

                const canUpdateSilently =
                    isOnlineRef.current && !queueBusyRef.current && !hasUserInteracted() && !wasAutoUpdatedRecently();

                if (canUpdateSilently && (await requestActivationIfSoleClient(waitingServiceWorker))) {
                    const activated = await waitForActivation(waitingServiceWorker);
                    // The user may have started typing meanwhile – then ask instead of reloading under them
                    if (activated && !hasUserInteracted() && rememberAutoUpdate()) {
                        reloadPage();
                        return;
                    }
                }

                showDialog(registration.waiting ?? waitingServiceWorker);
            },
        });
    }, []);

    if (dialogVisible) {
        return (
            <Dialog
                closable={true}
                // Closing must not activate the new version: this page keeps running the old code, and the new
                // service worker would drop the old files it may still load. The update is applied on next start.
                onClose={() => setDialogVisible(false)}
                title={t('update.dialog.title')}
                visible={dialogVisible}
                actions={[
                    <Button onClick={() => setDialogVisible(false)} ghost={true}>
                        {t('update.dialog.buttonNegative')}
                    </Button>,
                    <Button
                        onClick={() => {
                            // Already activated (e.g. by the automatic update at startup): just reload
                            if (serviceWorker?.state === 'activated') {
                                reloadPage();
                                return;
                            }
                            if (serviceWorker) {
                                serviceWorker.addEventListener('statechange', (event) => {
                                    if (event && event.target && 'state' in event.target) {
                                        if (event.target?.state === 'activated') {
                                            reloadPage();
                                        }
                                    }
                                });
                                serviceWorker.postMessage({ type: 'SKIP_WAITING' });
                            }
                        }}
                        primary={true}
                    >
                        {t('update.dialog.buttonPositive')}
                    </Button>,
                ]}
            >
                <div className={'max-w-md'}>
                    <p>{t('update.dialog.content')}</p>
                </div>
            </Dialog>
        );
    }

    return null;
};

export default CheckUpdates;
